import { BaseAPI } from "@utils/base-api";
import Axios from "axios";

import { EStatusCodes } from "@/constants/state";

import {
  IOrderLineItemResponse,
  IOrderReceiptResponse,
  TOrderLineItemsFilter,
  TOrderLineItemsWithQuantity,
} from "./types/orders.types";
import {
  GET_LAST_ORDER_LINE_ITEMS_PATH,
  GET_LAST_ORDER_LINE_ITEMS_PATH_PARAM,
  ORDERS_CUSTOMER_RECEIPTS_GET_ALL_PATH,
  ORDERS_CUSTOMER_RECEIPTS_GET_ONE_PATH,
  ORDERS_CUSTOMER_RECEIPTS_GET_ONE_PATH_PARAM,
} from "./endpoints/customer";
import {
  ORDERS_INTERNAL_HAS_PURCHASED_NIBBLES_IN_X_MONTHS_PATH,
} from "./endpoints/internal";

export interface HasNibblesRecentlyResponse {
  hasPurchasedNibbles: boolean;
  lastOrderDate: string | null;
}

export class OrdersAPI extends BaseAPI {

  public async hasPurchasedNibblesInXMonths(months: number): Promise<HasNibblesRecentlyResponse> {
    const result = await this.axios.get<HasNibblesRecentlyResponse>(
      ORDERS_INTERNAL_HAS_PURCHASED_NIBBLES_IN_X_MONTHS_PATH,
      {
        params: { months },
      },
    );
    return result.data;
  }

  public async getAllOrderReceipts(): Promise<IOrderReceiptResponse[]> {
    try {
      const result = await this.axios.get(
        ORDERS_CUSTOMER_RECEIPTS_GET_ALL_PATH,
      );
      return result.data;
    } catch (error) {
      if (Axios.isAxiosError(error) && error.response?.status === EStatusCodes.NOT_FOUND) {
        return [];
      }
      throw error;
    }
  }

  public async getOrderReceipt(orderId: number): Promise<IOrderReceiptResponse | null> {
    try {
      const result = await this.axios.get(
        ORDERS_CUSTOMER_RECEIPTS_GET_ONE_PATH.replace(`:${ORDERS_CUSTOMER_RECEIPTS_GET_ONE_PATH_PARAM}`, orderId.toString()),
      );
      return result.data;
    } catch (error) {
      if (Axios.isAxiosError(error) && error.response?.status === EStatusCodes.NOT_FOUND) {
        return null;
      }
      throw error;
    }
  }

  public async getAllOrderLineItems(): Promise<IOrderLineItemResponse | null[]> {
    const result = await this.axios.get(
      "/customer/orders/items/all",
    );
    return result.data;
  }

  /**
   * Get the last order line items for a subscription.
   * @param subscriptionId - The ID of the subscription.
   * @param filter - The filter to specify which items to return: "RECIPES", "EXTRAS", "ALL". Default is "ALL".
   * @returns The order line items with quantity.
   */
  public async getLastOrderLineItems(
    subscriptionId: number,
    filter?: TOrderLineItemsFilter,
  ): Promise<TOrderLineItemsWithQuantity | null> {
    const result = await this.axios.get(
      GET_LAST_ORDER_LINE_ITEMS_PATH
        .replace(`:${GET_LAST_ORDER_LINE_ITEMS_PATH_PARAM}`, subscriptionId.toString()),
      {
        params: { filter },
      },
    );
    return result.data;
  }

  public async hadHalloweenRecipe(subscriptionId: number): Promise<boolean> {
    const result = await this.axios.get(
      `/customer/orders/had-halloween-recipe/${subscriptionId}`,
    );
    return result.data;
  }

  public async hadXmasRecipe(subscriptionId: number): Promise<boolean> {
    const result = await this.axios.get(
      `/customer/orders/had-xmas-recipe/${subscriptionId}`,
    );
    return result.data;
  }

}
